import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'
import { DomainDto } from '../../../../../dtos/domain'

type AudienceDomain = Pick<DomainDto, 'id' | 'displayName'>
type PageAnalysisCandidate = AudienceDomain & Pick<DomainDto, 'hasPageAnalysis' | 'notificationFieldSettings'>

/** The order the audience picker lists an organization's domains in. */
const byDisplayName = <T extends AudienceDomain>(domains: ReadonlyArray<T>): T[] =>
    [...domains].sort((a, b) =>
        (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' }),
    )

/**
 * The domains the notification is going to, in order.
 *
 * A domain-level form goes to its own domain. An org-level form goes to the audience's chosen
 * domains in the order they were chosen, or to every creatable org domain in display-name order
 * (the picker's order) when the audience is "All Domains" (`audienceDomainIds` unset). A chosen
 * id that is not a creatable domain is dropped. Empty while the creatable list is empty or still
 * loading, or while "Specific Domains" has nothing picked yet.
 */
export function resolveSelectedDomains<T extends AudienceDomain>(
    level: PlatformLevel,
    defaultDomain: T,
    audienceDomainIds: readonly number[] | undefined,
    availableDomains: ReadonlyArray<T>,
): T[] {
    if (level === 'domain') {
        return [defaultDomain]
    }

    if (!audienceDomainIds) {
        return byDisplayName(availableDomains)
    }

    return audienceDomainIds
        .map((id) => availableDomains.find((d) => d.id === id))
        .filter((d): d is T => d !== undefined)
}

/**
 * The domain that stands in for the notification wherever a single domain is needed (keyword and
 * macro options, the preview, the submit route): the first selected domain, when the audience
 * names one. The view falls back to the org's first domain until it does.
 */
export function resolveFirstSelectedDomain<T extends AudienceDomain>(
    level: PlatformLevel,
    defaultDomain: T,
    audienceDomainIds: readonly number[] | undefined,
    availableDomains: ReadonlyArray<T>,
): T | undefined {
    return resolveSelectedDomains(level, defaultDomain, audienceDomainIds, availableDomains)[0]
}

/**
 * Whether Page Analysis can fill anything on a domain: the feature is on and at least one field
 * is enabled for it (a null column or a null source means every field).
 */
export function domainOffersPageAnalysis(domain: PageAnalysisCandidate): boolean {
    if (!domain.hasPageAnalysis) return false
    const enabledFields = domain.notificationFieldSettings?.content_suggestions
    return domain.notificationFieldSettings == null || enabledFields == null || enabledFields.length > 0
}

/**
 * The domain Page Analysis runs under: its flag, its enabled fields, the analyze request and the
 * landing URL macro substitution all read off it.
 *
 * A domain-level form uses its own domain when the feature is on there (the "no fields enabled"
 * case still shows, as a disabled menu item, so the user learns why). An org-level form scans the
 * selected domains in order for the first one Page Analysis can fill anything on; none means the
 * control is hidden, since analyzing under a domain outside the audience would be wrong.
 */
export function resolvePageAnalysisDomain<T extends PageAnalysisCandidate>(
    level: PlatformLevel,
    defaultDomain: T,
    audienceDomainIds: readonly number[] | undefined,
    availableDomains: ReadonlyArray<T>,
): T | undefined {
    if (level === 'domain') {
        return defaultDomain.hasPageAnalysis ? defaultDomain : undefined
    }

    return resolveSelectedDomains(level, defaultDomain, audienceDomainIds, availableDomains).find(
        domainOffersPageAnalysis,
    )
}
